/* eslint-disable max-lines */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { ga4addonSelected } from "../../../analytics/gaEvents";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import Toast from "../../atoms/toast";
import { constants } from "../../../constants/pageConstants/pdpConstants";
import Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../../molecules/carouselHelpers";
import { handlePdpSelectedAddon } from "../../../store/slices/pdpAddonSlice";
import AddonCard from "./addonCard";
import { formatClassNames } from "../../../utils/common";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";

const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;

/**
 * Renders a single category chip for add-on filtering. Defined outside
 * AddonBody and memoized so it doesn't remount on every addon add/remove.
 *
 * @param {Object} props - Component props
 * @param {Object} props.category - Category to display
 * @param {string} props.activeCategory - Currently active category
 * @param {boolean} props.isMobile - Whether rendering in mobile layout
 * @param {Function} props.onCategorySelection - Handler for selecting a category
 * @returns {JSX.Element} Rendered Chip component
 */
const RenderChip = React.memo(({ category, activeCategory, isMobile, onCategorySelection }) => {
  return (
    <div
      className={`relative flex shrink-0 cursor-pointer flex-col items-center gap-4 pb-8 ${isMobile ? "min-w-[70px]" : ""} ${activeCategory === category?.categoryName ? "after:absolute after:bottom-0 after:left-0 after:h-[4px] after:w-full after:rounded-t-full after:bg-fnp-300 after:content-['']" : ""}`}
      onClick={() => onCategorySelection(category.categoryName)}
    >
      <div className="h-[40px] w-[40px] shrink-0 overflow-hidden rounded-full border-1 border-greySoft">
        <CustomImage
          src={`${imageDomain}/${category.categoryImageUrl}`}
          width={40}
          height={40}
          priority={false}
          alt={category?.categoryName}
          customStyle={{ width: "100%", height: "100%" }}
        />
      </div>
      <Typography classes={`font-500 text-14 text-black-80 text-center line-clamp-1 leading-tight`} variant="p" Tag="p">
        {category.categoryName}
      </Typography>
    </div>
  );
});
RenderChip.displayName = "RenderChip";
RenderChip.propTypes = {
  category: PropTypes.shape({
    categoryName: PropTypes.string,
    categoryImageUrl: PropTypes.string,
  }).isRequired,
  activeCategory: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  onCategorySelection: PropTypes.func.isRequired,
};

/**
 * Component that renders the main body for add-ons, displaying
 * available add-ons and managing user interactions with them.
 *
 * @returns {JSX.Element} The rendered add-on body component.
 */
const AddonBody = ({
  commonCleverTapData,
  isFromPDP = false,
  isMobile,
  isBottomDrawer = false,
  isFromPopUp = false,
  pdpAddonViewedKeyRef,
  isTrackingSuppressed = false,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [activeCategory, setActiveCategory] = useState("All");
  const [isShowMaxAddonToast, setIsShowMaxAddonToast] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  // Selecting a date on PDP resets/refetches pdpAddon state, which unmounts and
  // remounts AddonBody. A ref local to this component would reset with it and
  // let addonViewed re-fire for the same pid/pincode/date, so the PDP caller
  // (and popup drawer) passes down a ref that survives across those remounts.
  const localAddonViewedKeyRef = useRef("");
  const lastTrackedAddonViewKeyRef = pdpAddonViewedKeyRef || localAddonViewedKeyRef;
  const isPopupSurface = Boolean(isFromPopUp || isBottomDrawer || !isFromPDP);

  const {
    selectedCurrency,
    pdpAddonInfo,
    pdpSelectedAddons,
    checkLoginStatus,
    pincode,
    selectedCartProduct,
    selectedDate,
  } = useSelector(
    (state) => ({
      selectedCurrency: state?.currencyList?.userSelectedCurrency,
      pdpAddonInfo: state.pdpAddon?.addOnInfo,
      pdpSelectedAddons: state.pdpAddon?.selectedAddons,
      checkLoginStatus: state.fus.loginDetail?.loggedin,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      selectedCartProduct: state?.cart?.selectedCartProduct,
      selectedDate: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails?.selectedDate,
    }),
    shallowEqual,
  );

  const handleAddonDispatch = useCallback(
    (data) => {
      dispatch(handlePdpSelectedAddon(data));
    },
    [dispatch],
  );

  const addonInfo = pdpAddonInfo;
  const addonsList = addonInfo.addonsList?.[0]?.products;
  const organizedAddons = addonInfo.allOrganizedAddonsList;
  const selectedAddons = pdpSelectedAddons;

  const categories = useMemo(() => {
    const allCategory = {
      categoryName: "All",
      categoryImageUrl: addonsList ? addonsList[0]?.imageUrl : null,
    };

    const categoryList =
      organizedAddons?.map((item) => {
        const categoryData = item[Object.keys(item)[0]];
        let imageUrl = categoryData.categoryImageUrl;
        if (!imageUrl) {
          const firstProductId = categoryData.addOnProductIds?.[0];
          const matchedAddon = addonsList?.find((addon) => addon.productId === firstProductId);
          imageUrl = matchedAddon?.imageUrl || matchedAddon?.productImage || matchedAddon?.productImageUrl || null;
        }
        return {
          categoryName: categoryData.categoryName,
          categoryImageUrl: imageUrl,
        };
      }) || [];

    return [allCategory, ...categoryList];
  }, [organizedAddons, addonsList]);

  const filteredAddons = useMemo(() => {
    if (activeCategory === "All") return addonsList;
    return addonsList?.filter((item) =>
      organizedAddons.some(
        (category) =>
          category[Object.keys(category)[0]].categoryName === activeCategory &&
          category[Object.keys(category)[0]].addOnProductIds.includes(item.productId),
      ),
    );
  }, [activeCategory, addonsList, organizedAddons]);

  /** */
  const chunkArray = (array, size) => {
    const chunked = [];
    for (let i = 0; i < array?.length; i += size) {
      chunked.push(array?.slice(i, i + size));
    }
    return chunked;
  };

  const chunkedAddons = useMemo(() => chunkArray(filteredAddons, 2), [filteredAddons]);

  useEffect(() => {
    let selectedAddOn = [];
    let addOn = selectedCartProduct?.addons;

    if (!isEmpty(addOn) && !isEmpty(addonsList)) {
      addonsList?.forEach((element1) => {
        addOn?.forEach((element2) => {
          if (element1.productId === element2.productId) {
            selectedAddOn.push({
              ...element1,
              quantity: element2.quantity,
              itemIndex: element2.itemIndex,
              isFromCart: true,
            });
          }
        });
      });
    }
    handleAddonDispatch(selectedAddOn);
  }, [addonsList, handleAddonDispatch, selectedCartProduct]);

  // Track Addon Viewed once per context. Popup/drawer uses surviving ref so remounts don't multi-fire.
  useEffect(() => {
    if (isTrackingSuppressed || isEmpty(addonsList)) {
      return;
    }

    const viewKey = isPopupSurface
      ? `${commonCleverTapData?.pid || ""}|popup`
      : `${commonCleverTapData?.pid || ""}|${pincode || ""}|${selectedDate || ""}`;

    if (!viewKey || lastTrackedAddonViewKeyRef.current === viewKey) {
      return;
    }
    lastTrackedAddonViewKeyRef.current = viewKey;

    const addonViewedPayload = {
      ...commonCleverTapData,
      is_date_today: null,
      is_date_tomorrow: null,
      is_variant_selected: null,
      addon_viewed_from_popup: isPopupSurface,
      flow: "new",
    };
    delete addonViewedPayload.delivery_shipping_fee;
    trackEvent(cleverTapCommonEvents.addonViewed, addonViewedPayload, ["clevertap"]);
    // Intentionally omit commonCleverTapData — it changes on most PDP interactions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addonsList,
    isFromPDP,
    isFromPopUp,
    isBottomDrawer,
    isPopupSurface,
    isTrackingSuppressed,
    pincode,
    selectedDate,
    commonCleverTapData?.pid,
    trackEvent,
  ]);

  const ADDON_SLIDER_ROWS = 2;
  const visibleSlides = 2.3;
  // With rows: 2, slick indexes by column — not by individual product cards
  const totalSlides = Math.max(1, Math.ceil((filteredAddons?.length || 0) / ADDON_SLIDER_ROWS));

  useEffect(() => {
    setActiveSlide(0);
  }, [activeCategory]);

  const addonHeadingSlideConfig = {
    dots: false,
    infinite: false,
    slidesToShow: 5.5,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    // variableWidth: true,
  };
  const addonSlideConfig = {
    dots: false,
    infinite: false,
    slidesToShow: visibleSlides,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    rows: ADDON_SLIDER_ROWS,
    beforeChange: (_, next) => setActiveSlide(next),
  };

  /**
   * Handles the selection of a category and updates the active category.
   *
   * @param {string} category - The selected category to filter the addon products.
   */
  const getSelectedItemQuantity = (productId) => {
    const selectedItem = selectedAddons.find((item) => item.productId === productId);
    return selectedItem ? selectedItem.quantity : 0;
  };

  /**
   * Hides the max addon toast.
   */
  const onToggleAddonToast = () => {
    setIsShowMaxAddonToast(false);
  };

  /**
   * Renders the toast notification based on the error state.
   */
  const renderToast = () => (
    <Toast
      variant="info"
      isToast={true}
      duration={3000}
      header={constants.MAX_1_QUANTITY_ALLOWED}
      onToastChange={onToggleAddonToast}
    />
  );

  /**
   * Updates the quantity of a selected add-on or adds it to the list if it is not found.
   *
   * @param {Object} addon - The add-on item whose quantity is to be changed.
   * @param {number} count - The new quantity for the add-on.
   * @param {boolean} [isDisabled] - Whether add is blocked (e.g. max qty).
   * @param {boolean} [isAddedFromAddonPreview=false] - True when add is from addon preview drawer.
   */
  const handleItemCountChange = (addon, count, isDisabled, isAddedFromAddonPreview = false) => {
    let isFound = false;
    if (isDisabled) {
      if (!isShowMaxAddonToast) {
        setIsShowMaxAddonToast(true);
      }
    } else {
      const value = selectedAddons.map((item) => {
        if (item.productId === addon.productId) {
          isFound = true;
          if (item.quantity < count) {
            trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Plus") }, [
              "ga4",
            ]);
          } else {
            trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Minus") }, [
              "ga4",
            ]);
          }
          return {
            ...item,
            quantity: count,
          };
        }
        return item;
      });

      if (isFound) {
        handleAddonDispatch(value);
      } else {
        handleAddonDispatch([...pdpSelectedAddons, { ...addon, quantity: count }]);
      }
      const addonAddedPayload = {
        ...commonCleverTapData,
        addon_name: addon?.productName,
        addon_price: addon?.price,
        addon_quantity: count,
        addon_type: addon?.productType,
        is_date_today: null,
        is_date_tomorrow: null,
        is_variant_selected: null,
        addon_added_from_popup: isPopupSurface,
        is_added_from_addon_preview: Boolean(isAddedFromAddonPreview),
        tab_name: activeCategory,
        flow: "new",
      };
      delete addonAddedPayload.delivery_shipping_fee;
      trackEvent(cleverTapCommonEvents.addonAdded, addonAddedPayload, ["clevertap"]);
      trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Plus") }, [
        "ga4",
      ]);
    }
  };

  /**
   * Removes an add-on item from the selected add-ons list.
   *
   * @param {Object} addon - The add-on item to be removed, identified by its productId.
   */
  const onRemoveItem = (addon) => {
    let value = [];
    selectedAddons.forEach((item) => {
      if (item.isFromCart && item.productId == addon.productId) {
        value.push({ ...item, quantity: 0 });
      } else {
        if (item.productId !== addon.productId) {
          value.push(item);
        }
      }
    });
    handleAddonDispatch(value);
    const addonRemovedPayload = {
      ...commonCleverTapData,
      addon_price: addon?.price,
      addon_name: addon?.productName,
      addon_quantity: addon?.configItemIndex,
      is_date_today: null,
      is_date_tomorrow: null,
      is_variant_selected: null,
      addon_added_from_popup: isPopupSurface,
      flow: "new",
    };
    delete addonRemovedPayload.delivery_shipping_fee;
    trackEvent(cleverTapCommonEvents.addonRemoved, addonRemovedPayload, ["clevertap"]);
    trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Minus") }, ["ga4"]);
  };

  /**
   * Updates the active category based on the user's selection.
   *
   * @param {string} category - The category selected by the user.
   */
  const onCategorySelection = useCallback(
    (category) => {
      setActiveCategory(category);
      trackEvent(
        cleverTapCommonEvents.addonTabSelected,
        {
          ...commonCleverTapData,
          is_date_today: null,
          is_date_tomorrow: null,
          is_variant_selected: null,
          addon_added_from_popup: isPopupSurface,
          tab_name: category,
        },
        ["clevertap"],
      );
    },
    [commonCleverTapData, isPopupSurface, trackEvent],
  );

  return (
    <>
      {isShowMaxAddonToast && renderToast()}
      {isFromPDP && !isMobile ? (
        <Slider
          {...addonHeadingSlideConfig}
          nextArrow={
            <SlickNextArrow
              customStyle={{
                border: "1px solid #EEE",
              }}
              right="0px"
            />
          }
          prevArrow={
            <SlickPrevArrow
              customStyle={{
                border: "1px solid #EEE",
              }}
              left="0px"
            />
          }
          className="addonChips"
          style={{ borderBottom: "1px solid #EEE" }}
        >
          {categories?.map((category) => (
            <div key={category.categoryName} style={{ width: "auto" }}>
              <RenderChip
                category={category}
                activeCategory={activeCategory}
                isMobile={isMobile}
                onCategorySelection={onCategorySelection}
              />
            </div>
          ))}
        </Slider>
      ) : (
        <div
          className={formatClassNames(
            "category scrollbar-hide flex h-[12%] min-h-[80px] w-full gap-[16px] overflow-x-auto border-b-1 border-thinLightGrey bg-white-900 px-[16px] pb-0 pt-10 md:min-h-[100px] md:gap-[40px] md:pb-0 md:pt-12 md:scrollbar-thin",
            isFromPDP ? "" : "sticky top-0 z-50 border-b-1 border-greySoft",
            isBottomDrawer ? "mb-0 pb-0 pt-0" : "mb-[16px]",
          )}
        >
          {categories?.map((category) => (
            <RenderChip
              key={category.categoryName}
              category={category}
              activeCategory={activeCategory}
              isMobile={isMobile}
              onCategorySelection={onCategorySelection}
            />
          ))}
        </div>
      )}
      {isFromPDP ? (
        !isMobile ? (
          <div className="block w-full">
            <Slider
              key={activeCategory}
              {...addonSlideConfig}
              nextArrow={
                <SlickNextArrow
                  customStyle={{
                    border: "1px solid #EEE",
                  }}
                  right="0px"
                />
              }
              prevArrow={
                <SlickPrevArrow
                  customStyle={{
                    border: "1px solid #EEE",
                  }}
                  left="0px"
                />
              }
            >
              {filteredAddons?.map((item) => {
                const addonsQty = getSelectedItemQuantity(item.productId);
                const isDisabled = addonsQty === 1 && item.price === 0;
                return (
                  <AddonCard
                    key={item.productId}
                    item={item}
                    addonsQty={addonsQty}
                    isDisabled={isDisabled}
                    isFromPDP={isFromPDP}
                    isMobile={isMobile}
                    selectedCurrency={selectedCurrency}
                    onRemoveItem={onRemoveItem}
                    handleItemCountChange={handleItemCountChange}
                    isBottomDrawer={isBottomDrawer}
                  />
                );
              })}
            </Slider>
            {totalSlides > visibleSlides && (
              <div className="mt-12 flex justify-center">
                <div className="h-[4px] w-1/2 overflow-hidden rounded-full bg-grey-300">
                  <div
                    className="h-full rounded-full bg-fnp-600 transition-all duration-300"
                    style={{
                      width: `${Math.min(((activeSlide + visibleSlides) / totalSlides) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="scrollbar-hide flex gap-16 overflow-x-auto px-16">
            {chunkedAddons.map((pair, i) => (
              <div key={i} className="flex w-[28%] flex-none flex-col items-start gap-8 p-2 md:w-[14.9%] xxl:w-[15.5%]">
                {pair.map((item) => {
                  const addonsQty = getSelectedItemQuantity(item.productId);
                  const isDisabled = addonsQty === 1 && item.price === 0;
                  return (
                    <AddonCard
                      key={item.productId}
                      item={item}
                      addonsQty={addonsQty}
                      isDisabled={isDisabled}
                      isFromPDP={isFromPDP}
                      isMobile={isMobile}
                      selectedCurrency={selectedCurrency}
                      onRemoveItem={onRemoveItem}
                      handleItemCountChange={handleItemCountChange}
                      isBottomDrawer={isBottomDrawer}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )
      ) : (
        <div
          className={`scrollbar-hide flex ${isBottomDrawer ? "h-[70vh]" : "h-[100vh]"} flex-wrap justify-between overflow-x-auto overflow-y-auto px-[16px] pb-[35vh] pt-16 md:justify-start md:gap-20 md:pb-0 md:pt-8 md:scrollbar-thin`}
        >
          {filteredAddons?.map((item) => {
            const addonsQty = getSelectedItemQuantity(item.productId);
            const isDisabled = addonsQty === 1 && item.price === 0;
            return (
              <AddonCard
                key={item.productId}
                item={item}
                addonsQty={addonsQty}
                isDisabled={isDisabled}
                isFromPDP={isFromPDP}
                isMobile={isMobile}
                selectedCurrency={selectedCurrency}
                onRemoveItem={onRemoveItem}
                handleItemCountChange={handleItemCountChange}
                isBottomDrawer={isBottomDrawer}
                isFromPopUp={isFromPopUp}
              />
            );
          })}
        </div>
      )}
    </>
  );
};
AddonBody.propTypes = {
  commonCleverTapData: PropTypes.object,
  isFromPDP: PropTypes.bool,
  isMobile: PropTypes.bool,
  isBottomDrawer: PropTypes.bool,
  isFromPopUp: PropTypes.bool,
  pdpAddonViewedKeyRef: PropTypes.shape({ current: PropTypes.string }),
  isTrackingSuppressed: PropTypes.bool,
};

export default AddonBody;
